import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  AuthStatusResponse,
  companyAuthStatusApiClient,
} from '@src/api/company/auth/companyAuthStatusApiClient';
import { companyDxDiagnosisAnswerApiClient } from '@src/api/company/dxDiagnosis/dxDiagnosisAnswerApiClient';
import { companyDxDiagnosisStepApiClient } from '@src/api/company/dxDiagnosis/dxDiagnosisStepApiClient';
import { DxDiagnosisRequestAnswer } from '@src/api/types/dxDiagnosisAnswer';
import { DxDiagnosisQuestion } from '@src/api/types/dxDiagnosisQuestion';
import { DxDiagnosisStep } from '@src/api/types/dxDiagnosisStep';
import SpinnerOverlay from '@src/components/SpinnerOverlay';
import { useAsyncHandler } from '@src/hooks/useAsyncHandler';
import { useUnsavedChanges } from '@src/hooks/useUnsavedChange';
import { MAX_NUMBER_OF_QUESTIONS, QUESTIONS_PER_STEP } from '@src/modules/constants/dxDiagnosis';
import { COMPANY_URL } from '@src/routes/company';
import Button from '@src/ui/elements/button/Button';
import ConfirmLeaveModal from '@src/ui/templates/Shared/ConfirmLeaveModal';
import logger from '@src/utils/logger';
import { useEffect, useRef, useState } from 'react';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom';

import Questions from './components/Questions';
import { StatusItem, StatusParent, StepbarStatus } from './components/StatusItem';

interface DxDiagnosisRequestAnswerWithSaved extends DxDiagnosisRequestAnswer {
  is_saved: boolean;
}

interface CompanyDxDiagnosisQuestionFormProps {
  onUnansweredPage: () => void;
}

function CompanyDxDiagnosisQuestionForm({ onUnansweredPage }: CompanyDxDiagnosisQuestionFormProps) {
  const [steps, setSteps] = useState<DxDiagnosisStep[]>([]);
  const [questions, setQuestions] = useState<DxDiagnosisQuestion[]>([]);
  const [userAnswers, setUserAnswers] = useState<DxDiagnosisRequestAnswerWithSaved[]>([]);
  const [lastAnsweredStep, setLastAnsweredStep] = useState<number | null>(null);
  const [isUnansweredPage, setIsUnansweredPage] = useState<boolean>(false);
  const [showValidation, setShowValidation] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { id, stepId } = useParams<{ id: string; stepId: string }>();
  const [loginStatus, setLoginStatus] = useState<AuthStatusResponse>();

  const targetQuestionId = location.state?.questionId;

  useEffect(() => {
    const fetchLoginStatus = async () => {
      const res = await companyAuthStatusApiClient.status();
      handleApiResponse(res, async (data) => {
        setLoginStatus(data);
      });
    };
    fetchLoginStatus();
  }, []);

  const questionRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const scrollToQuestion = (questionId: number) => {
    if (questionRefs.current[questionId]) {
      const element = questionRefs.current[questionId];
      if (element) {
        const headerHeight =
          document.querySelector('.dx-diagnosis-step')?.getBoundingClientRect().height || 0;

        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        if (elementPosition && headerHeight > 0) {
          window.scrollTo({
            top: elementPosition - (headerHeight + 30),
            behavior: 'smooth',
          });
        }
      }
    }
  };

  const scrollToFirstUnansweredQuestion = () => {
    const firstUnansweredQuestion = questions.find(
      (q) => !userAnswers.some((a) => a.dx_diagnosis_question_id === q.id),
    );
    if (firstUnansweredQuestion) {
      scrollToQuestion(firstUnansweredQuestion.id);
    }
  };

  const hasScrolledRef = useRef(false);

  useEffect(() => {
    if (questions.length > 0 && userAnswers.length > 0 && !hasScrolledRef.current) {
      if (targetQuestionId) {
        const targetQuestion = questions.find((q) => q.id === targetQuestionId);
        if (targetQuestion) {
          setTimeout(() => {
            scrollToQuestion(targetQuestionId);
          }, 100);
        }
      } else {
        scrollToFirstUnansweredQuestion();
      }
      hasScrolledRef.current = true;
    }
  }, [questions.length > 0 && userAnswers.length > 0, targetQuestionId]);

  const { loading: isLoadingQuestions, execute: fetchQuestions } = useAsyncHandler(async () => {
    const response = await companyDxDiagnosisStepApiClient.show(Number(stepId));
    handleApiResponse(response, (data: DxDiagnosisQuestion[]) => {
      setQuestions(data);
      if (!targetQuestionId) {
        window.scrollTo(0, 0);
        hasScrolledRef.current = false;
      }
      if (currentStep?.order && userAnswers.length > 0) {
        const hasAnsweredCurrentStep = data.some((question) =>
          userAnswers.some((answer) => answer.dx_diagnosis_question_id === question.id),
        );

        if (
          hasAnsweredCurrentStep &&
          lastAnsweredStep !== null &&
          currentStep.order > lastAnsweredStep
        ) {
          setLastAnsweredStep(currentStep.order);
        }
      }
    });
  });

  useEffect(() => {
    hasScrolledRef.current = false;
    fetchQuestions();
  }, [stepId]);

  const { loading: isLoadingStepbar, execute: fetchStepbarData } = useAsyncHandler(async () => {
    const response = await companyDxDiagnosisStepApiClient.index();
    handleApiResponse(response, (data: DxDiagnosisStep[]) => {
      setSteps(data);
    });
  });

  useEffect(() => {
    fetchStepbarData();
  }, []);

  const { loading: isLoadingDxDiagnosisData, execute: fetchDxDiagnosisData } = useAsyncHandler(
    async () => {
      const response = await companyDxDiagnosisAnswerApiClient.index({
        dx_diagnosis_result_id: Number(id),
      });
      handleApiResponse(
        response,
        (data) => {
          const initialAnswers: DxDiagnosisRequestAnswerWithSaved[] = [];
          data.map((answer) => {
            answer.questions.map((question) => {
              const answer = {
                dx_diagnosis_result_id: Number(id),
                dx_diagnosis_question_id: question.question_id,
                dx_diagnosis_choice_id: question.answer.id,
                is_saved: true,
              };

              initialAnswers.push(answer);
            });
          });

          if (initialAnswers.length === 0) {
            hasScrolledRef.current = true;
          }

          if (data && initialAnswers.length > 0 && data[0].is_finished) {
            navigate(generatePath(COMPANY_URL.DX_DIAGNOSIS_RESULTS, { id }));
            return;
          }

          setUserAnswers(initialAnswers);

          const lastStep = data.find((answer) =>
            answer.questions.some(
              (question) =>
                question.question_id ===
                initialAnswers[initialAnswers.length - 1].dx_diagnosis_question_id,
            ),
          )?.step.id;

          setLastAnsweredStep(lastStep ? Math.floor(initialAnswers.length / 5) * 5 : 0);
        },
        (error) => {
          logger.error(error);
          console.error('DX診断データを取得できませんでした', error);
        },
      );
    },
  );

  useEffect(() => {
    fetchDxDiagnosisData();
  }, [id, loginStatus?.user?.id]);

  useEffect(() => {
    if (!steps.length || lastAnsweredStep === null || !stepId) {
      return;
    }

    const currentStep = steps.find((step) => step.id === Number(stepId));
    if (!currentStep || currentStep.order > lastAnsweredStep + 5) {
      setIsUnansweredPage(true);
    }
  }, [steps, lastAnsweredStep, stepId, navigate]);

  const currentStep = steps.find((step) => step.id === Number(stepId));

  const handleAnswerSelect = (questionId: number, choiceId: number) => {
    const newAnswers = [
      ...userAnswers.filter((answer) => answer.dx_diagnosis_question_id !== questionId),
      {
        dx_diagnosis_result_id: Number(id),
        dx_diagnosis_question_id: questionId,
        dx_diagnosis_choice_id: choiceId,
        is_saved: false,
      },
    ];
    setUserAnswers(newAnswers);
  };

  const { execute: saveAnswers } = useAsyncHandler(async () => {
    let saveAnswersSuccess: boolean = false;

    const userAnswersWithoutSavedStatus = userAnswers.map((answer) => ({
      dx_diagnosis_result_id: answer.dx_diagnosis_result_id,
      dx_diagnosis_question_id: answer.dx_diagnosis_question_id,
      dx_diagnosis_choice_id: answer.dx_diagnosis_choice_id,
    }));

    const response = await companyDxDiagnosisAnswerApiClient.create(userAnswersWithoutSavedStatus);
    handleApiResponse(
      response,
      () => {
        const userAnswerWithSavedStatus = userAnswers.map((answer) => ({
          ...answer,
          is_saved: true,
        }));

        setUserAnswers(userAnswerWithSavedStatus);
        saveAnswersSuccess = true;
      },
      () => {
        saveAnswersSuccess = false;
      },
    );

    return saveAnswersSuccess;
  });

  const navigateStep = async ({
    stepOffset = 5,
    isNavigateToLastStep,
  }: {
    stepOffset?: number;
    isNavigateToLastStep?: boolean;
  }) => {
    const saveAnswersResult = await saveAnswers();
    if (!saveAnswersResult) {
      return;
    }

    setShowValidation(false);

    const targetStep = !isNavigateToLastStep
      ? steps.find((step) => step.order === (currentStep?.order || 0) + stepOffset)
      : null;

    if (targetStep) {
      navigate(
        generatePath(COMPANY_URL.DX_DIAGNOSIS_QUESTION, {
          id: id!,
          stepId: targetStep.id.toString(),
        }),
      );
    } else {
      navigate(
        stepOffset > 0
          ? generatePath(COMPANY_URL.DX_DIAGNOSIS_ANSWERS, { id: id! })
          : generatePath(COMPANY_URL.DX_DIAGNOSIS, {}),
      );
    }
  };

  const handleNextStep = async () => {
    if (questions.some((q) => !userAnswers.some((a) => a.dx_diagnosis_question_id === q.id))) {
      setShowValidation(true);
      await new Promise((resolve) => setTimeout(resolve, 0));
      scrollToFirstUnansweredQuestion();
      return;
    }

    if (currentStep?.order && lastAnsweredStep !== null && currentStep.order > lastAnsweredStep) {
      setLastAnsweredStep(currentStep.order);
    }

    await navigateStep({});
  };

  const handlePrevStep = async () => {
    if (stepId === '1') {
      await saveAnswers();
      navigate(`/dx-diagnosis/explanation?id=${id}`);
      return;
    }
    navigateStep({ stepOffset: -5 });
  };

  const handleStepNavigation = (targetStep: DxDiagnosisStep) => {
    const targetUrl = generatePath(COMPANY_URL.DX_DIAGNOSIS_QUESTION, {
      id: id!,
      stepId: targetStep.id.toString(),
    });

    navigate(targetUrl);
  };

  const { isModalOpen, confirmReturn, cancelReturn } = useUnsavedChanges({
    hasUnsavedChanges: userAnswers.some((answer) => !answer.is_saved),
    onConfirm: () => {
      const savedAnswersOnly = userAnswers.filter((answer) => answer.is_saved);
      setUserAnswers(savedAnswersOnly);
      setShowValidation(false);
      window.history.back();
    },
  });

  useEffect(() => {
    if (isUnansweredPage) {
      onUnansweredPage();
    }
  }, [isUnansweredPage, onUnansweredPage]);

  if (isLoadingStepbar || isLoadingDxDiagnosisData || isLoadingQuestions) {
    return <SpinnerOverlay loading={true} />;
  }

  return (
    <>
      <main className='bg-[#f9f9f9] pb-20'>
        <StatusParent>
          {steps
            .sort((a, b) => a.order - b.order)
            .map((step, index) => {
              let status: StepbarStatus = 'not-begin';

              const stepIndex = step.order / QUESTIONS_PER_STEP - 1;
              const stepStartAnswerIndex = stepIndex * QUESTIONS_PER_STEP;
              const stepEndAnswerIndex = stepStartAnswerIndex + QUESTIONS_PER_STEP;

              const stepSavedAnswers = userAnswers
                .filter((answer) => answer.is_saved)
                .slice(stepStartAnswerIndex, stepEndAnswerIndex);

              const isStepFullyCompleted = stepSavedAnswers.length === QUESTIONS_PER_STEP;

              if (lastAnsweredStep !== null && step.order <= lastAnsweredStep) {
                status = isStepFullyCompleted ? 'completed' : 'in-progress';
              } else if (step.order === currentStep?.order) {
                status = 'in-progress';
              } else if (
                lastAnsweredStep !== null &&
                step.order === lastAnsweredStep + QUESTIONS_PER_STEP &&
                userAnswers.filter((a) => a.is_saved).length >= stepStartAnswerIndex
              ) {
                status = 'in-progress';
              }

              if (step.order === currentStep?.order) {
                status = `${status}-current` as StepbarStatus;
              }

              return (
                <StatusItem
                  key={step.code}
                  label={`${step.order / 5}. ${step.name}`}
                  lineDirection={index === 0 ? 'right' : undefined}
                  status={status}
                  onClick={() => {
                    if (status !== 'not-begin') {
                      handleStepNavigation(step);
                    }
                  }}
                  disabled={status === 'not-begin'}
                />
              );
            })}
          <StatusItem
            label='回答確認'
            lineDirection='left'
            status={lastAnsweredStep === MAX_NUMBER_OF_QUESTIONS ? 'in-progress' : 'not-begin'}
            onClick={() => {
              if (lastAnsweredStep !== MAX_NUMBER_OF_QUESTIONS) {
                return;
              }
              navigateStep({ isNavigateToLastStep: true });
            }}
            disabled={lastAnsweredStep !== MAX_NUMBER_OF_QUESTIONS}
          />
        </StatusParent>

        <h1 className='pt-[50px] text-center text-xl font-semibold leading-none lg:pt-[111px]'>
          {currentStep && `${currentStep.order / 5}. ${currentStep.name}`}
        </h1>
        <div className='mt-[30px] flex flex-col gap-[30px]'>
          <Questions
            questions={questions}
            userAnswers={userAnswers}
            handleAnswerSelect={handleAnswerSelect}
            showValidation={showValidation}
            questionRefs={questionRefs}
          />
        </div>

        {steps.length > 0 && questions.length > 0 && (
          <div className='mt-8 flex justify-center gap-[30px]'>
            <div className='w-[172px]'>
              <Button
                size='m'
                variant='primary'
                outline
                fullRounded
                fullWidth
                onClick={handlePrevStep}
              >
                前に戻る
              </Button>
            </div>
            <div className='w-[172px]'>
              <Button
                size='m'
                variant='primary'
                fullRounded
                fullWidth
                onClick={handleNextStep}
                data-testid='next-button'
              >
                {Number(stepId) === 6 ? '回答を確認する' : '次に進む'}
              </Button>
            </div>
          </div>
        )}

        <ConfirmLeaveModal
          title='まだ保存されていません。'
          message='現在回答中のページの回答は保存されていません。
このまま続行すると回答内容が破棄されますがよろしいですか？'
          confirmText='回答内容を破棄して閉じる'
          cancelText='回答を続行'
          isOpen={isModalOpen}
          onConfirm={confirmReturn}
          onClose={cancelReturn}
        />
      </main>
    </>
  );
}

export default CompanyDxDiagnosisQuestionForm;
